import { useEffect, useState } from "preact/hooks";
import { onPeonSpeech, speechTextFor, type PlayingClip } from "./audio.ts";

/**
 * What the peon is saying, as text, for the speech bubble in the header (src/components/Shell.tsx
 * Header): `speaking` while a line plays, then `last` for SPEECH_LINGER_MS after it ends so the
 * bubble is read before it goes, then nothing. Fed by src/audio.ts's speech notifications; the words
 * come from lines.json beside the voice files and the tour manifest. The Warcraft set has no
 * transcript, so it never sets anything.
 */
export const SPEECH_LINGER_MS = 2500;

interface SpeechState {
  speaking: string | null;
  last: string | null;
  /** The clip playing `speaking`, so the bubble can follow its progress; null once it ends. */
  audio: PlayingClip | null;
}

let state: SpeechState = { speaking: null, last: null, audio: null };
const listeners = new Set<() => void>();
let subscribed = false;
let linger: ReturnType<typeof setTimeout> | null = null;

function emit(next: SpeechState): void {
  state = next;
  for (const listener of listeners) listener();
}

type Notifier = typeof onPeonSpeech;
type TextLookup = typeof speechTextFor;

/**
 * Starts following the peon's speech. Idempotent. The two hooks exist so a test can wire the store
 * to its own audio module; the app always uses the real ones.
 */
export function subscribeSpeech(notify: Notifier = onPeonSpeech, textFor: TextLookup = speechTextFor): void {
  if (subscribed) return;
  subscribed = true;
  notify((audio, dir, name) => {
    void textFor(dir, name).then((text) => {
      if (!text) return;
      if (linger !== null) {
        clearTimeout(linger);
        linger = null;
      }
      const done = () => {
        if (state.speaking !== text) return;
        emit({ speaking: null, last: text, audio: null });
        linger = setTimeout(() => {
          linger = null;
          if (state.speaking === null && state.last === text) emit({ speaking: null, last: null, audio: null });
        }, SPEECH_LINGER_MS);
      };
      emit({ speaking: text, last: text, audio });
      if (audio.ended) {
        done();
        return;
      }
      // "pause" covers a line cut short by a later one (src/audio.ts stops it), not just the end.
      audio.addEventListener("ended", done, { once: true });
      audio.addEventListener("pause", done, { once: true });
    });
  });
}

/** How long a line said in text alone stays in the bubble. */
export const TEXT_LINE_MS = 4000;

/**
 * Puts `text` in the peon's bubble with no recording behind it (the workshop's "Stewards only. Me
 * just peon.", docs/members.md), for TEXT_LINE_MS; a spoken line that starts meanwhile replaces it.
 */
export function sayText(text: string): void {
  if (linger !== null) clearTimeout(linger);
  emit({ speaking: null, last: text, audio: null });
  linger = setTimeout(() => {
    linger = null;
    if (state.speaking === null && state.last === text) emit({ speaking: null, last: null, audio: null });
  }, TEXT_LINE_MS);
}

export function getSpeech(): SpeechState {
  return state;
}

export function useSpeech(): SpeechState {
  const [current, setCurrent] = useState(state);
  useEffect(() => {
    subscribeSpeech();
    const update = () => setCurrent(state);
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return current;
}
